import { Component, OnInit } from '@angular/core';
import { ActivatedRoute} from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { EssayCandidateModel, MarkingQuestionModel, IExam, AdjustQuestionScoreModel, EssayCandidateResponseModel } from 'src/app/interface/dto';
import { ExamScheduleService } from 'src/app/services/exam-schedule-service';
import { ExamService } from 'src/app/services/exam-service.service';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { ScoreAdjustmentModalComponent } from '../../Shared/score-adjustment-modal/score-adjustment-modal.component';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog.service';
import { DomSanitizer } from '@angular/platform-browser';

@Component({
  selector: 'app-mark-essay',
  templateUrl: './mark-essay.component.html',
  styleUrls: ['./mark-essay.component.scss'],
})
export class MarkEssayComponent implements OnInit {
  mode: string = 'mark';
  candidates: EssayCandidateModel[] = [];
  questions: MarkingQuestionModel[] = [];
  pickedQuestion: MarkingQuestionModel;
  selectedCandidate: EssayCandidateModel;
  totalScore: number;
  examination: IExam;
  candidateCount: number = 0;
  candidateMarkedCount: number = 0;
  candidateUnmarkedCount: number = 0;
  showPendingOnly: boolean = false;

  constructor(
    private snackBar: SnackbarService,
    private examService: ExamService,
    private examSchService: ExamScheduleService,
    private route: ActivatedRoute,
    private modalService: NgbModal,
    private confirmDialog: ConfirmDialogService, 
    public sanitizer: DomSanitizer
  ) {}

  get totalMarkedCandidates() {
    return this.candidates.filter((cand) => cand.marked).length;
  }
  get totalUnMarkedCandidates() {
    return this.candidates.filter((cand) => !cand.marked).length;
  }
  get totalCandidatesReviewed() {
    return this.candidates.filter((cand) => cand.reviewed).length;
  }
  get totalCandidatesPending() {
    return this.candidates.filter((cand) => !cand.reviewed).length;
  }

  get totalScheduleEssaySubQuestionScores(): number {
    if (!this.pickedQuestion?.hasEssaySubQuestions || !this.pickedQuestion?.scheduleEssaySubQuestions) {
      return 0;
    }

    return this.pickedQuestion.scheduleEssaySubQuestions
      .map((sQuestion) => Number(sQuestion.markEarned) || 0)
      .reduce((a, b) => a + b, 0);
  }

  async ngOnInit() {
    this.route.queryParams.subscribe((params) => {
      this.mode = params['mode'] || 'mark';
    });

    let selectedExamID = this.examService.selectedExamIds[0];
    this.examination = this.examService.selectedExams[0];
    let selectedSchID =
      (this.examSchService.selectedIds && this.examSchService.selectedIds[0]) ??
      0;
    let data = {
      ExaminationScheduleID: selectedSchID,
      ExaminationID: selectedExamID,
      Mode: this.mode,
    };

    const response = (await this.examService.getEssayCandidate(
      data
    )) as EssayCandidateResponseModel;
    this.candidates = response.essayCandidates;
    this.candidateCount = response.candidateCount;
    this.candidateMarkedCount = response.candidateMarkedCount;
    this.candidateUnmarkedCount = response.candidateUnmarkedCount;
  }

  get filteredCandidates(): EssayCandidateModel[] {
    if (!this.showPendingOnly) return this.candidates;

    return this.candidates.filter(cand =>
      this.mode === 'review' ? !cand.reviewed : !cand.marked
    );
  }

  onPendingFilterChange() {
    const stillVisible = this.filteredCandidates.find(
      c => c.examNo === this.selectedCandidate?.examNo
    );

    if (!stillVisible && this.filteredCandidates.length > 0) {
      this.loadAllQuestions(this.filteredCandidates[0]);
    }
  }

  async loadAllQuestions(cand: EssayCandidateModel) {
    this.questions = [];
    this.pickedQuestion = null;
    this.selectedCandidate = cand;
    let data = {
      ExaminationID: this.examination.id,
      ExaminationScheduleID: cand.examinationScheduleID,
      Mode: this.mode,
    };

    const buttons = document.querySelectorAll('.canlist');
    buttons.forEach((button) => {
      button.classList.remove('btn-warning');
    });
    const activeButton = document.getElementById(cand.examNo);
    if (activeButton) {
      activeButton.classList.add('btn-warning');
    }

    this.questions = (await this.examService.getMarkEssayQuestions(
      data
    )) as any[];
    if (this.questions.length > 0) {
      this.loadQuestion(this.questions[0].esqId);
    } else {
      await this.markCandidateWithNoQuestions();
    }
  }

  loadQuestion(id) {
    this.pickedQuestion = this.questions.find((x) => x.esqId == id);
    if (!this.pickedQuestion) return;
    this.calculateTotalScore();
  }

  hasNextQuestion() {
    if (!this.questions || this.questions.length === 0) {
      return false;
    }
    const currentIndex = this.questions.findIndex(
      (q) => q.esqId === this.pickedQuestion.esqId
    );
    return currentIndex < this.questions.length - 1;
  }

  hasPreviousQuestion() {
    if (!this.questions || this.questions.length === 0) {
      return false;
    }
    const currentIndex = this.questions.findIndex(
      (q) => q.esqId === this.pickedQuestion.esqId
    );
    return currentIndex > 0;
  }

  nextQuestion() {
    const currentIndex = this.questions.findIndex(
      (q) => q.esqId === this.pickedQuestion.esqId
    );
    if (currentIndex < this.questions.length - 1) {
      this.loadQuestion(this.questions[currentIndex + 1].esqId);
    }
  }
  previousQuestion() {
    const currentIndex = this.questions.findIndex(
      (q) => q.esqId === this.pickedQuestion.esqId
    );
    if (currentIndex > 0) {
      this.loadQuestion(this.questions[currentIndex - 1].esqId);
    }
  }

  calculateTotalScore() {
    this.totalScore = this.questions.reduce(
      (acc, q) => acc + (q.markEarned || 0),
      0
    );
  }

  async markEssay() {
    const currentIndex = this.questions.findIndex(
      (q) => q.esqId === this.pickedQuestion.esqId
    );

    if (this.pickedQuestion.scheduleEssaySubQuestions) {
         // Validate that all sub question have scores
      const invalidSubQuestions = this.pickedQuestion.scheduleEssaySubQuestions.filter(
        sQuestion => sQuestion.markEarned === null || sQuestion.markEarned === undefined
      );

      if (invalidSubQuestions.length > 0) {
        this.snackBar.error('Please enter scores for all sub-question');
        return;
      }
      let hasEmpty = false;
      let subText = '';
      // Additional check to ensure no empty values and to add error border to empty inputs
      this.pickedQuestion.scheduleEssaySubQuestions.forEach((_, i) => {
        const input = document.getElementById(`subQuestion-score-${i}`) as HTMLInputElement;
        input.classList.remove('error-border');
        if (!input || input.value === '' || input.value === null || input.value === undefined || isNaN(Number(input.value))) {
          hasEmpty = true;
          if (input) {
            subText += `\nSub-question ${_.questionNoLabel} score is required.`;
            input.classList.add('error-border');
          }
        }
      });

      if (hasEmpty) {
        this.snackBar.error('Please enter scores for all sub-question for the question. ' + subText);
        return;
      }

      const overScoredSubQuestions = this.pickedQuestion.scheduleEssaySubQuestions.filter(sQuestion => sQuestion.markEarned > sQuestion.marks);

  if (overScoredSubQuestions.length > 0) {
    const labels = overScoredSubQuestions
      .map(sQuestion => sQuestion.questionNoLabel || '?')
      .join(', ');
    this.snackBar.error(`Scores for sub-question(s) ${labels} exceed their maximum marks`);
    return;
  }

      const sectionTotal = this.totalScheduleEssaySubQuestionScores;

      if (sectionTotal > this.pickedQuestion.mark) {
        this.snackBar.error('Total section scores exceed the maximum mark');
        return;
      }

      // Update the total score for the question based on section scores
      this.pickedQuestion.markEarned = sectionTotal;
    } else {
      if (
        this.pickedQuestion.markEarned === null ||
        this.pickedQuestion.markEarned === undefined
      ) {
        this.snackBar.error('Please enter a score');
        return;
      }

      if (this.pickedQuestion.markEarned > this.pickedQuestion.mark) {
        this.snackBar.error(
          'Score should be less than or equal to total marks'
        );
        return;
      }
    }

    this.questions[currentIndex].markEarned =
      this.pickedQuestion.markEarned;
    this.questions[currentIndex].scheduleEssaySubQuestions =
      this.pickedQuestion.scheduleEssaySubQuestions;

    this.examService.markEssayQuestion(this.pickedQuestion).then(() => {
      this.snackBar.success('Scored Successfully');
      this.questions[currentIndex].marked =true;
    });
    this.calculateTotalScore();
  }

  openAdjustScoreModal() {
    const modalRef = this.modalService.open(ScoreAdjustmentModalComponent, {
      centered: true,
      scrollable: true,
    });

    modalRef.componentInstance.currentScore =
      this.pickedQuestion.markEarned;
    modalRef.componentInstance.maxScore = this.pickedQuestion.mark;
    modalRef.componentInstance.questionNo = this.pickedQuestion.questionNo;
    modalRef.componentInstance.questionId = this.pickedQuestion.esqId;
    modalRef.componentInstance.examinationType = 'Essay';
    modalRef.componentInstance.scheduleEssaySubQuestions =
      this.pickedQuestion.scheduleEssaySubQuestions;
    modalRef.componentInstance.hasessaysubquestions =
      this.pickedQuestion.hasEssaySubQuestions;

    modalRef.result.then(
      (result: AdjustQuestionScoreModel) => {
        if (result) {
          if (
            !this.pickedQuestion.hasEssaySubQuestions &&
            result.adjustedScore === this.pickedQuestion.markEarned
          ) {
            this.snackBar.warning(
              'No adjustment applied since the current score matches the adjusted score.'
            );
            return;
          }
          this.adjustScore(result);
        }
      },
      (reason) => {
        // Modal dismissed
      }
    );
  }

  async adjustScore(adjustmentData: AdjustQuestionScoreModel) {
    try {
      await this.examService.adjustQuestionScore(adjustmentData);
      const currentIndex = this.questions.findIndex(
        (q) => q.esqId === adjustmentData.esqId
      );
      if (currentIndex !== -1) {
        if (
          this.pickedQuestion.hasEssaySubQuestions &&
          adjustmentData.scheduleEssaySubQuestions 
        ) {
        
         adjustmentData.scheduleEssaySubQuestions.forEach((adjustedSubQuestion, index) => {
          if (this.questions[currentIndex].scheduleEssaySubQuestions[index]) {
            this.questions[currentIndex].scheduleEssaySubQuestions[index].markEarned = 
              adjustedSubQuestion.markEarned;
          }
        });
        
        this.questions[currentIndex].markEarned =
             this.questions[currentIndex].scheduleEssaySubQuestions.reduce(
              (sum, sQuestion) => sum + (sQuestion.markEarned || 0),
              0
            );
        this.questions[currentIndex].marked = true;

          this.loadQuestion(this.questions[currentIndex].esqId);
        } else {
          this.questions[currentIndex].markEarned =
            adjustmentData.adjustedScore;
        }

        const index = this.candidates.findIndex(
          (c) => c.examNo === this.selectedCandidate.examNo
        );
        if (index !== -1) {
          this.candidates[index].reviewed = false;
        }
        this.calculateTotalScore();
      }

      this.snackBar.success('Score adjusted successfully');
    } catch (error) {
      this.snackBar.error('Failed to adjust score');
    }
  }

  hasNextCandidate() {
    if (
      !this.filteredCandidates  ||
      this.filteredCandidates.length === 0 ||
      !this.selectedCandidate
    ) {
      return false;
    }
    const currentIndex = this.filteredCandidates.findIndex(
      (c) => c.examNo === this.selectedCandidate.examNo
    );
    return currentIndex < this.candidates.length - 1;
  }

  hasPreviousCandidate() {
    if (
      !this.filteredCandidates  ||
      this.filteredCandidates.length === 0 ||
      !this.selectedCandidate
    ) {
      return false;
    }
    const currentIndex = this.filteredCandidates.findIndex(
      (c) => c.examNo === this.selectedCandidate.examNo
    );
    return currentIndex > 0;
  }

  nextCandidate() {
    if (!this.hasNextCandidate()) {
      return;
    }

    const currentIndex = this.filteredCandidates.findIndex(
      (c) => c.examNo === this.selectedCandidate.examNo
    );

    if (!this.filteredCandidates[currentIndex].marked && this.mode === 'mark') {
      this.snackBar.success('Ensure to submit once grading is done');
    }

    const nextCandidate = this.filteredCandidates[currentIndex + 1];
    this.loadAllQuestions(nextCandidate);
  }

  previousCandidate() {
    if (!this.hasPreviousCandidate()) {
      return;
    }

    const currentIndex = this.filteredCandidates.findIndex(
      (c) => c.examNo === this.selectedCandidate.examNo
    );
    const prevCandidate = this.filteredCandidates[currentIndex - 1];
    this.loadAllQuestions(prevCandidate);
  }

  async openSubmitConfirmation(event: MouseEvent) {
    if (!this.selectedCandidate) {
      this.snackBar.error('Please select a candidate first');
      return;
    }

    let unmarkedQuestions = this.questions.filter(q => !q.marked);
    if (unmarkedQuestions.length > 0) {
      let questionNumbers = unmarkedQuestions.map(q => q.questionNo).join(', ');
      this.snackBar.error(`There are unmarked questions.
        \n Question numbers with unmarked scores ${unmarkedQuestions.length > 1 ? 'are' : 'is'}: ${questionNumbers}`);
      return;
    }

    const confirmed = await this.confirmDialog.open({
      message:
        'Are you sure you want to submit marks for this candidate?<br>Submitting means essay scores cannot be updated afterward.',
      confirmText: 'Yes, Submit',
      cancelText: 'Cancel',
      size: 'lg',
      centered: true,
      title: 'Confirm Submission',
    });

    if (confirmed) {
      this.submitCandidateMarks();
    }
  }

  async submitCandidateMarks() {
    try {
      await this.examService.setEssayAsMarked(
        this.examService.selectedExamIds[0],
        this.selectedCandidate.examinationScheduleID
      );

      const index = this.candidates.findIndex(
        (c) => c.examNo === this.selectedCandidate.examNo
      );
      if (index !== -1) {
        this.candidates[index].marked = true;
      }

      this.snackBar.success('Candidate scores submitted successfully');
      
      const allDone = this.mode === 'review'
        ? this.candidates.every(c => c.reviewed)
        : this.candidates.every(c => c.marked);
        
        if (allDone) {
          this.promptRequestScript();
        }
    } catch (error) {
      this.snackBar.error('Failed to submit candidate marks');
    }
  }

  isReadOnly(): boolean {
    return (
      this.mode === 'review' ||
      (this.mode === 'mark' && this.selectedCandidate?.marked)
    );
  }

  async SetAsReviewed() {
    try {
      const response = (await this.examService.setAsReviewed(
        this.examService.selectedExamIds[0],
        this.selectedCandidate.examinationScheduleID
      )) as any;

      const index = this.candidates.findIndex(
        (c) => c.examNo === this.selectedCandidate.examNo
      );
      if (index !== -1) {
        this.candidates[index].reviewed = true;
        this.candidates[index].reviewer = String(response.data);
      }

      const allDone = this.candidates.every(c => c.reviewed);
        
        if (allDone) {
          this.promptRequestScript();
        }

      this.snackBar.success('Marked as review successfully');
    } catch (error) {
      this.snackBar.error('Failed to set as Reviewed');
    }
  }

    loadingMoreScripts = false;
  
    async requestMoreScripts() {
      this.loadingMoreScripts = true;
  
      try {
        const res = this.mode === 'review' ?
        await this.examService.requestMoreEssayScriptsForReview(this.examination.id) as EssayCandidateResponseModel
        : await this.examService.requestMoreEssayScriptsForScoring(this.examination.id) as EssayCandidateResponseModel;
        
          if (res.essayCandidates.length < 1) {
          this.snackBar.info('No new scripts available at the moment');
          return;
        }
  
        const existing = new Set(this.candidates.map(c => c.examNo));
        const newScripts = res.essayCandidates.filter(c => !existing.has(c.examNo));
  
        this.candidates = [
          ...this.candidates,
          ...newScripts
        ];

        this.candidateCount += newScripts.length;
  
        this.snackBar.success(`${newScripts.length} new script(s) assigned`);

        this.candidateMarkedCount = Math.max(0, this.candidateMarkedCount - newScripts.length);
        setTimeout(() => {
          this.loadAllQuestions(newScripts[0]);
        }, 0);
      } catch {
        this.snackBar.error('Failed to request more scripts');
      } finally {
        this.loadingMoreScripts = false;
      }
  }

  async promptRequestScript() {
    const confirmed = await this.confirmDialog.open({
      title: 'Request New Script?',
      message: 'Assessment submitted successfully.<br>Would you like to request a new script?',
      confirmText: 'Yes, Request Script',
      cancelText: 'No, I\'m done',
      size: 'lg',
      centered: true,
      confirmButtonClass: 'btn-primary'
    });

    if (confirmed) {
      this.requestMoreScripts();
    }
  }

  private async markCandidateWithNoQuestions() {
    try {
      const index = this.candidates.findIndex(
        (c) => c.examNo === this.selectedCandidate.examNo
      );

      if (this.mode === 'mark' && !this.candidates[index]?.marked) {
        await this.examService.setEssayAsMarked(
          this.examination.id,
          this.selectedCandidate.examinationScheduleID,
        );

        if (index !== -1) {
          this.candidates[index].marked = true;
        }

        this.snackBar.info('Candidate has no answered questions. Marked automatically.');
      } else if (this.mode === 'review' && !this.candidates[index]?.reviewed) {
        const response = await this.examService.setAsReviewed(
          this.examService.selectedExamIds[0],
          this.selectedCandidate.examinationScheduleID
        ) as any;

        if (index !== -1) {
          this.candidates[index].reviewed = true;
          this.candidates[index].reviewer = String(response.data);
        }

        this.snackBar.info('Candidate has no answered questions. Reviewed automatically.');
      }
    } catch (error) {
      this.snackBar.error(`Failed to auto-${this.mode === 'review' ? 'review' : 'mark'} candidate with no questions`);
    }
  }

    getDisplayScore(question: any): number | null {
        if (!question) return null;

        // Only show value if marked or in review mode or if there is a score (for cases when marks are entered before)
        if (question.marked || this.mode == 'review' || question.markEarned > 0) {
            return question.markEarned ?? 0;
        }
        return null;
    }

}
